import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Search, MessageSquare, User, Shield, Loader2, ChevronDown } from "lucide-react";

export default function ChatHistory() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [sessions, setSessions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [fullSession, setFullSession] = useState(null);

  useEffect(() => { if (!loading && (!user || !user.is_admin)) nav("/auth"); }, [user, loading, nav]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (query) params.q = query;
      if (kind) params.kind = kind;
      const r = await api.get("/admin/chat/sessions", { params });
      setSessions(r.data.sessions || []);
    } catch { toast.error("Failed to load chat history"); }
  }, [query, kind]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const openSession = async (id) => {
    if (expanded === id) { setExpanded(null); setFullSession(null); return; }
    setExpanded(id);
    setFullSession(null);
    try {
      const r = await api.get(`/admin/chat/sessions/${id}`);
      setFullSession(r.data);
    } catch { toast.error("Failed to load session"); }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="chat-history-page">
      <div className="flex items-center gap-3 mb-2">
        <MessageSquare size={22} className="text-[color:var(--hb-royal)]"/>
        <h1 className="font-display text-4xl text-[color:var(--hb-navy)]">Chat History</h1>
      </div>
      <p className="text-sm text-neutral-600 mb-6">Every past conversation — yours and customers'. Search across all messages to find that idea you brainstormed.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder="Search messages…" className="w-full pl-9 pr-3 py-2 border border-neutral-300 text-sm" data-testid="chat-search"/>
        </div>
        <select value={kind} onChange={e => setKind(e.target.value)} className="border border-neutral-300 px-3 py-2 text-sm" data-testid="kind-filter">
          <option value="">All chats</option>
          <option value="admin">My chats</option>
          <option value="customer">Customer chats</option>
        </select>
        <button onClick={load} className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-3 py-2" data-testid="search-btn">Search</button>
      </div>

      {sessions.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-neutral-300 text-sm text-neutral-500" data-testid="chat-empty">No conversations match your filter.</div>
      ) : (
        <div className="space-y-2" data-testid="chat-list">
          {sessions.map(s => {
            const isOpen = expanded === s.session_id;
            const KindIcon = s.kind === "admin" ? Shield : User;
            return (
              <div key={s.session_id} className="border border-neutral-200 bg-white" data-testid={`session-${s.session_id}`}>
                <button onClick={() => openSession(s.session_id)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-neutral-50">
                  <KindIcon size={14} className={s.kind === "admin" ? "text-[color:var(--hb-royal)]" : "text-neutral-500"}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{s.preview || "(empty)"}</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5 uppercase tracking-widest">
                      {s.kind} · {s.message_count} messages · {new Date(s.updated_at).toLocaleString()}
                    </div>
                  </div>
                  <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                </button>
                {isOpen && (
                  <div className="border-t border-neutral-100 p-4" data-testid={`session-${s.session_id}-details`}>
                    {!fullSession ? (
                      <div className="text-center py-6 text-xs text-neutral-500"><Loader2 size={14} className="animate-spin inline mr-2"/> Loading…</div>
                    ) : (
                      <div className="space-y-3">
                        {(fullSession.messages || []).map((m, i) => (
                          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[color:var(--hb-navy)] text-white" : "bg-neutral-100 text-neutral-900"}`}>
                              {m.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8"><Link to="/admin" className="text-xs text-[color:var(--hb-royal)] uppercase tracking-widest">← Admin home</Link></div>
    </div>
  );
}
